// measureTool.js — shared "Measure" click-drag tool for both charts (Dashboard's
// SignalChart.js and the More Info chart in App.js). Mirrors TradingView's Measure tool:
// arm it, click-drag between two points on the price pane, see price change / % change /
// candle-count readout. ONE definition used by both charts (same pattern as
// chartHelpers.js) so this isn't a third copy-pasted implementation.
//
// v1 scope:
// - Persisted per user × symbol × chart context (`dashboard` vs `more_info` kept separate,
//   same convention as chart_drawings) via Supabase `chart_measurements` (migration 0006).
//   Deliberately NOT localStorage — syncs across devices for the same signed-in user, same
//   as the horizontal-line drawing tool. Requires userId; the button is hidden when signed
//   out (mirrors Draw Line).
// - ONE active measurement per symbol/context — placing a new one overwrites the last, and
//   there's a small × on the box to clear it explicitly. No multi-touch/mobile (mouse only).
// - While armed, chart pan/zoom (handleScroll/handleScale) is disabled during the drag
//   itself (toggled off on mousedown, restored on mouseup) so dragging measures instead of
//   panning the chart.
//
// Anchoring: the two endpoints are stored as DATA coordinates (time + price), not pixel
// coordinates. Pixel position is re-projected from that data on every render, and a
// subscription to the chart's visible-range change re-triggers that projection whenever the
// user pans or zooms — so the line stays pinned to the same two candles instead of drifting.

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchMeasurement, saveMeasurement, deleteMeasurement } from "./measurementsStore";

// Turns a candle count + the chart's own granularity into a human time span in the
// chart's native unit — e.g. 50 candles on a 1h chart reads as "50 hours" (not a
// generic duration like "2d 2h"), 50 candles on a 6h chart reads as "300 hours", 50
// candles on a 1d chart reads as "50 days". Picks whichever unit the granularity is a
// whole multiple of (day > hour > minute) so the number always lines up 1:1 with what's
// on the timeframe selector.
function formatCandleTimeSpan(bars, granularitySeconds) {
  if (bars == null || !granularitySeconds) return null;
  const abs = Math.abs(bars);
  let unitSeconds, unitLabel;
  if (granularitySeconds % 86400 === 0) { unitSeconds = 86400; unitLabel = "day"; }
  else if (granularitySeconds % 3600 === 0) { unitSeconds = 3600; unitLabel = "hour"; }
  else { unitSeconds = 60; unitLabel = "minute"; }
  const totalUnits = Math.round((abs * granularitySeconds) / unitSeconds);
  return `${totalUnits} ${unitLabel}${totalUnits === 1 ? "" : "s"}`;
}

// chartRef / seriesRef: refs to the lightweight-charts chart instance + price series.
// containerRef: ref to the chart's DOM container (for mouse event coordinates + listeners).
// granularitySeconds: seconds-per-candle for the active timeframe (from granularityMap),
// used to turn the time delta between the two points into an approximate candle count.
// userId/symbol/context: identify which saved row to read/write (context: "dashboard" | "more_info").
export function useMeasureTool({ chartRef, seriesRef, containerRef, granularitySeconds, userId, symbol, context }) {
  const [measureMode, setMeasureMode] = useState(false);
  const [dataPoints, setDataPoints] = useState(null); // {time1, price1, time2, price2} — DATA space, survives pan/zoom
  const [, setRangeTick] = useState(0); // bumped on pan/zoom so the pixel projection below recomputes
  const draggingRef = useRef(false);
  const dataPointsRef = useRef(null); // mirrors dataPoints — read inside the mouseup handler to avoid stale closures

  // Load this user/symbol/context's saved measurement whenever any of those change
  // (including on first mount / page refresh). Independent of measureMode — a saved
  // measurement should show up right away, before the user re-arms the tool.
  useEffect(() => {
    let cancelled = false;
    dataPointsRef.current = null;
    setDataPoints(null);
    if (!userId || !symbol || !context) return;
    fetchMeasurement(userId, symbol, context)
      .then((row) => {
        if (cancelled || !row) return;
        const loaded = { time1: row.time1, price1: row.price1, time2: row.time2, price2: row.price2 };
        dataPointsRef.current = loaded;
        setDataPoints(loaded);
      })
      .catch((e) => console.error("[chart_measurements-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = chart.timeScale().coordinateToTime(x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!measureMode || !container) return;

    const handleMouseDown = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      draggingRef.current = true;
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      const next = { time1: p.time, price1: p.price, time2: p.time, price2: p.price };
      dataPointsRef.current = next;
      setDataPoints(next);
    };
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;
      const p = pointFromEvent(e);
      if (!p) return;
      const prev = dataPointsRef.current;
      if (!prev) return;
      // Update the ref synchronously (not just inside the setState updater) — mouseup reads
      // dataPointsRef.current directly, and React can defer the updater's execution, which
      // previously let a fast mousemove→mouseup sequence persist a stale (0-distance) point.
      const next = { ...prev, time2: p.time, price2: p.price };
      dataPointsRef.current = next;
      setDataPoints(next);
    };
    const handleMouseUp = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (!wasDragging) return; // stray mouseup elsewhere on the page — nothing to finish
      // Persist the finished measurement so it's there on any device the user signs into.
      if (userId && symbol && context && dataPointsRef.current) {
        saveMeasurement(userId, symbol, context, dataPointsRef.current)
          .catch((e) => console.error("[chart_measurements-save]", e?.message || e));
      }
      // Auto-disarm after one placement — unlike Draw Line (many lines, worth staying
      // armed for), Measure only ever holds ONE active measurement, so there's no benefit
      // to staying armed and it was blocking normal pan/zoom drags afterward.
      setMeasureMode(false);
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      // Safety: always restore normal pan/zoom when the tool is disarmed/unmounted,
      // even if a drag was interrupted mid-flight.
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    };
  }, [measureMode, containerRef, chartRef, pointFromEvent, userId, symbol, context]);

  // Explicit clear — the box's × button calls this. (Toggling measureMode off no longer
  // clears it; a saved measurement persists until the user removes it or draws a new one.)
  const clearMeasurement = useCallback(() => {
    dataPointsRef.current = null;
    setDataPoints(null);
    if (userId && symbol && context) {
      deleteMeasurement(userId, symbol, context).catch((e) => console.error("[chart_measurements-delete]", e?.message || e));
    }
  }, [userId, symbol, context]);

  // Re-project on pan/zoom: while a measurement exists, listen for visible-range changes
  // and force a re-render (the pixel projection below re-reads chartRef/seriesRef fresh on
  // every render, so this is enough to keep the line pinned to the same candles).
  // WATCHDOG version (2026-07-09) — re-attaches when the chart instance appears or is
  // recreated, immediate tick on attach. See trendLineTool.js comment for why.
  useEffect(() => {
    if (!dataPoints) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, dataPoints]);

  // Derived: current pixel projection (recomputed every render) + stats (computed once from
  // the underlying data, unaffected by pan/zoom).
  let measurement = null;
  let stats = null;
  if (dataPoints) {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (chart && series) {
      const x1 = chart.timeScale().timeToCoordinate(dataPoints.time1);
      const x2 = chart.timeScale().timeToCoordinate(dataPoints.time2);
      const y1 = series.priceToCoordinate(dataPoints.price1);
      const y2 = series.priceToCoordinate(dataPoints.price2);
      if (x1 != null && x2 != null && y1 != null && y2 != null) {
        measurement = { x1, y1, x2, y2 };
      }
    }
    const priceDiff = dataPoints.price2 - dataPoints.price1;
    const pctChange = dataPoints.price1 !== 0 ? (priceDiff / dataPoints.price1) * 100 : 0;
    const timeDiff = dataPoints.time2 - dataPoints.time1;
    const bars = granularitySeconds ? Math.round(timeDiff / granularitySeconds) : null;
    const timeSpan = formatCandleTimeSpan(bars, granularitySeconds);
    stats = { priceDiff, pctChange, bars, timeSpan };
  }

  return { measureMode, setMeasureMode, measurement, stats, clearMeasurement };
}

// SVG overlay — line between the two points + a floating stats box that hugs the line
// (directly above the higher point, or directly below the lower point if there's no room
// above) rather than jumping to the pane's top/bottom edge. Includes a small × to clear the
// saved measurement explicitly (it now persists until removed, not just while armed).
// Rendered as an absolutely-positioned sibling of the chart's DOM container (same pattern as
// the existing crosshair/legend overlay divs), zIndex 5 (below the load-error overlay at 6,
// above the chart canvas). Background is translucent + blurred so candles stay visible
// underneath.
export function MeasureOverlay({ measurement, stats, paneHeight = 400, onClear }) {
  if (!measurement || !stats) return null;
  const { x1, y1, x2, y2 } = measurement;
  const positive = stats.priceDiff >= 0;
  const color = positive ? "#4ade80" : "#f87171";
  const midX = (x1 + x2) / 2;
  const boxWidth = 150;
  const boxHeight = stats.timeSpan != null ? 62 : 50; // +1 line to also show the time span (e.g. "50 hours apart")
  const margin = 10;
  const topY = Math.min(y1, y2);
  const bottomY = Math.max(y1, y2);
  const placeAbove = topY - boxHeight - margin > 4;
  let boxY = placeAbove ? topY - boxHeight - margin : bottomY + margin;
  // Clamp so it can never render off the top/bottom edge of the pane.
  boxY = Math.max(4, Math.min(boxY, paneHeight - boxHeight - 4));
  return (
    <svg
      // pointerEvents: "none" on the SVG ROOT is critical — without it, this element's
      // full pane-covering bounding box (100% x 100%, zIndex 5) captures every mouse event
      // over the ENTIRE chart by default, even over the "empty" areas with no visible line
      // or box. That silently ate all mousedown/drag events meant for the chart underneath
      // — blocking new measurements from ever starting AND blocking normal pan/zoom — any
      // time a measurement was on screen (this was the real cause of "chart becomes
      // stagnant once I place a measurement"). The stats box re-enables pointer-events on
      // itself below so its × button stays clickable.
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      <g>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.5" strokeDasharray="4 3" />
        <circle cx={x1} cy={y1} r="3" fill={color} />
        <circle cx={x2} cy={y2} r="3" fill={color} />
      </g>
      {/* foreignObject is padded 10px beyond the visible box on every side so the ×
          button (which sits just outside the box's own corner) isn't clipped — SVG
          foreignObject clips its content strictly to its own x/y/width/height box. */}
      <foreignObject x={midX - boxWidth / 2 - 10} y={boxY - 10} width={boxWidth + 20} height={boxHeight + 20}>
        <div style={{
          position: "relative", margin: "10px", pointerEvents: "auto",
          background: "rgba(15,15,15,0.45)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
          border: `1px solid ${color}`, borderRadius: "6px",
          padding: "4px 8px", fontSize: "11px", color: "#eee", textAlign: "center", lineHeight: 1.5,
          fontFamily: "system-ui",
        }}>
          {onClear && (
            <button
              type="button"
              aria-label="Clear measurement"
              onClick={onClear}
              style={{
                position: "absolute", top: "-8px", right: "-8px", width: "16px", height: "16px",
                borderRadius: "50%", border: `1px solid ${color}`, background: "#111",
                color, fontSize: "10px", lineHeight: 1, cursor: "pointer", padding: 0,
              }}
            >
              ×
            </button>
          )}
          <div style={{ color, fontWeight: 600 }}>
            {positive ? "+$" : "-$"}{Math.abs(stats.priceDiff).toLocaleString(undefined, { maximumFractionDigits: 4 })}
            {" "}({positive ? "+" : ""}{stats.pctChange.toFixed(2)}%)
          </div>
          {stats.bars != null && (
            <div style={{ color: "#ccc" }}>{stats.bars} candle{Math.abs(stats.bars) === 1 ? "" : "s"} apart</div>
          )}
          {stats.timeSpan != null && (
            <div style={{ color: "#ccc" }}>{stats.timeSpan} apart</div>
          )}
        </div>
      </foreignObject>
    </svg>
  );
}
